import { AxiosRequestConfig } from "axios";

import { moneefloApi } from "./client";

/* The Xano signatures these stand in for were untyped, and the screens read
   fields off them freely. Narrowing here would only move the errors. */
/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * The dropdown lookups the lead and registration screens fill themselves
 * from: branches, partner channels, staff and contacts.
 *
 * All four were separate Xano tables. Here branches and partners are both
 * organizations — distinguished by type — and staff are users, so each of
 * these queries one endpoint and reshapes the rows into the option objects
 * the screens already map over.
 */

type Payload<T> = AxiosRequestConfig<T>;

type OrganizationRow = {
  id: string;
  name: string;
  type: string;
  city: string | null;
  state: string | null;
  status: string;
};

type CustomerRow = {
  id: string;
  customerNo: number;
  fullName: string;
  phone: string | null;
  email: string | null;
};

/**
 * Every row, not just the first page.
 *
 * Xano returned these lists whole and the screens filter them in the browser,
 * so a dropdown that stops at the API's hundred-row page silently loses
 * options — there are 338 branches and 973 partners, and the staff member
 * looking for the 400th has no way to tell it is missing rather than absent.
 * The ceiling is a guard against a runaway loop, not a real limit.
 */
const PAGE = 100;
const CEILING = 5000;

async function allPages<T>(
  path: string,
  params: Record<string, unknown> = {},
): Promise<T[]> {
  const rows: T[] = [];
  for (let page = 1; rows.length < CEILING; page += 1) {
    const { data } = await moneefloApi.get(path, {
      params: { ...params, page, perPage: PAGE },
    });
    const items = (data?.items ?? []) as T[];
    rows.push(...items);
    const total = Number(data?.total ?? rows.length);
    if (items.length < PAGE || rows.length >= total) break;
  }
  return rows;
}

async function onePage<T>(
  path: string,
  params: Record<string, unknown>,
): Promise<{ items: T[]; total: number }> {
  const { data } = await moneefloApi.get(path, { params });
  return {
    items: (data?.items ?? []) as T[],
    total: Number(data?.total ?? 0),
  };
}

async function organizationsOfType(
  type: string,
  params: Record<string, string> = {},
): Promise<OrganizationRow[]> {
  return allPages<OrganizationRow>("/organizations", { type, ...params });
}

/* -------------------------------------------------------------- branches */

export type GetBranchRegistrationRequestType = Partial<{
  headers: { Authorization: string };
  type: string;
}>;

/**
 * Our own branches, for the "which of our branches is placing this" picker.
 *
 * Scoped to the caller's own children. Xano's branch table held only ours,
 * but `organizations` holds every branch there is, partners' included, so an
 * unscoped list offered a hundred branches belonging to other businesses.
 *
 * The screen filters to `status === "active"` itself, so the status comes
 * across as-is rather than being filtered here — otherwise an inactive branch
 * would silently vanish twice over.
 */
export async function getBranchList(
  _payload: Payload<GetBranchRegistrationRequestType>,
): Promise<{ status: number; data: OrganizationRow[] }> {
  const branches = await organizationsOfType("branch", { parentId: "self" });
  return {
    status: 200,
    data: branches.map((branch) => ({
      id: branch.id,
      name: branch.name,
      city: branch.city,
      state: branch.state,
      type: branch.type,
      status: branch.status,
    })),
  };
}

/* -------------------------------------------------------------- channels */

export type GetChannelRequestType = Partial<{
  headers: { Authorization: string };
  q: string;
  page: number;
  per_page: number;
  status: string | string[];
  stage_tags: string;
}>;

/**
 * Partner channels, for the lead wizard's picker and the partners table.
 *
 * The wizard keeps only those that are active or pending *and* approved, so
 * both fields are supplied; approval is not modelled separately here, so an
 * active organization counts as approved.
 *
 * Two of the partners table's columns have nothing behind them. Xano stored a
 * street address on a channel and the import kept only the city and state, so
 * the location column shows the city; and the relationship manager was a
 * staff member named on the channel, which the new model does not record at
 * all, so that column stays empty rather than showing the wrong person.
 */
export async function getChannel(
  payload: Payload<GetChannelRequestType> & {
    params?: GetChannelRequestType;
  } = {},
): Promise<{ data: any }> {
  // Two callers want the whole list to filter in the browser and one wants a
  // page. Asking for a page is what tells them apart.
  const query = payload.params ?? {};
  const paged = Boolean(query.page);
  const page = Number(query.page) || 1;
  const perPage = Math.min(Number(query.per_page) || 100, 100);
  const search = query.q?.trim() || undefined;

  const partners = paged
    ? await onePage<OrganizationRow>("/organizations", {
        type: "partner",
        search,
        page,
        perPage,
      })
    : { items: await organizationsOfType("partner"), total: 0 };

  const total = paged ? partners.total : partners.items.length;
  const pageTotal = Math.max(1, Math.ceil(total / perPage));

  return {
    data: {
      pageTotal,
      curPage: page,
      itemsReceived: partners.items.length,
      itemsTotal: total,
      offset: (page - 1) * perPage,
      perPage,
      nextPage: paged && page < pageTotal ? page + 1 : null,
      prevPage: paged && page > 1 ? page - 1 : null,
      items: partners.items.map((partner) => ({
        id: partner.id,
        name: partner.name,
        city: partner.city,
        state: partner.state,
        type: partner.type,
        address_line_1: partner.city ?? "",
        _realtionship_manager_channels: null,
        status: partner.status,
        approval_status: partner.status === "active" ? "approved" : "pending",
      })),
    },
  };
}

/* ----------------------------------------------------------------- staff */

export type GetContactsForReferralReqType = Partial<{
  headers: { Authorization: string };
}>;

/** Everyone who can be picked as a colleague, referrer or assignee. */
/**
 * Colleagues work can be assigned to.
 *
 * Asks `/users/assignable` rather than `/users`: the latter is the
 * user-management list and needs `user.manage`, which ops and partner
 * staff do not have — so the reassign picker came back empty for exactly
 * the people whose job it is. The assignable list is already filtered to
 * active accounts in the caller's own organizations.
 */
async function activeStaff(): Promise<{ id: string; fname: string }[]> {
  const { data } = await moneefloApi.get("/users/assignable");
  const users = (data ?? []) as { id: string; fullName: string }[];
  return users.map((user) => ({ id: user.id, fname: user.fullName }));
}

/** Staff, for "who referred this" and "who contacted". */
export async function getContactsForReferral(
  _payload: Payload<GetContactsForReferralReqType>,
): Promise<{ data: { id: string; fname: string }[] }> {
  return { data: await activeStaff() };
}

export type GetRelationShipManagerRequestType = Partial<{
  q: string;
  headers: { Authorization: string };
}>;

export type RelationShipManager = { id: number | string; fname: string };

/**
 * The relationship-manager picker. Xano had this as its own endpoint on the
 * partner-registration API and it returned the same people as the staff list,
 * so it asks the same question here.
 */
export async function getRelationShipManager(
  _payload: Payload<GetRelationShipManagerRequestType>,
): Promise<{ data: RelationShipManager[] }> {
  return { data: await activeStaff() };
}

export type GetRMByProductCategoryRequestType = Partial<{
  category: string[];
  headers: { Authorization: string };
}>;

/**
 * The relationship-manager picker on the corporate lead screen. Xano
 * filtered by the services ticked; nobody is tied to a product on the new
 * API, so it offers the same people as every other manager picker.
 */
export async function getRMByProductCategory(
  _payload: Payload<GetRMByProductCategoryRequestType>,
): Promise<{ data: { id: string; fname: string }[] }> {
  return { data: await activeStaff() };
}

/* -------------------------------------------------------------- contacts */

export type GetContactsListReqType = Partial<{
  headers: { Authorization: string };
  searchQuery: string;
}>;

/**
 * Existing contacts, for "who referred this customer".
 *
 * The phone number rides along as `contactNumber`: the dropdown labels each
 * option "name - number", which is the only thing separating two people who
 * share a name.
 */
export async function getContactsList(
  payload: Payload<GetContactsListReqType> & {
    params?: { searchQuery?: string };
  },
): Promise<
  { data: { id: string; contactName: string; contactNumber: string }[] }
> {
  const search = (
    payload.params?.searchQuery ??
    payload.data?.searchQuery ??
    ""
  ).trim();

  const { data } = await moneefloApi.get("/customers", {
    params: { search: search || undefined, perPage: 50 },
  });
  const customers = (data?.items ?? []) as CustomerRow[];
  return {
    data: customers.map((customer) => ({
      id: customer.id,
      contactName: customer.fullName,
      contactNumber: customer.phone ?? "",
    })),
  };
}

/* ------------------------------------------------------------- currencies */

export type GetCurrencyRequestType = Partial<{
  headers: { Authorization: string };
}>;

/**
 * The currency list behind every "Enter amount" row.
 *
 * Xano kept one row per country, so the same currency appeared many times and
 * the screens de-duplicate it themselves. The catalog is shaped the same way,
 * and the country name is what the screens label the option with, so the rows
 * come across one-for-one rather than being collapsed here.
 */
/**
 * Every caller is a forex screen, and each one turns this into a currency
 * picker, so it answers with the currencies the counter actually trades -- one
 * row per currency, not one per country, which would have listed EUR twenty
 * times over. `country` carries the currency's own name ("Euro"), which is
 * what the two screens that read it want to show beside the code.
 */
export async function getCurrency(
  _payload: Payload<GetCurrencyRequestType>,
): Promise<{ data: { country: string; currency: string }[] }> {
  const { data } = await moneefloApi.get("/catalog/currencies", {
    params: { tradedOnly: true },
  });
  const currencies = (data ?? []) as { code: string; name: string }[];
  return {
    data: currencies.map((currency) => ({
      country: currency.name,
      currency: currency.code,
    })),
  };
}

/* ------------------------------------------------------------- all staff */

export type GetAllStaffRequestType = Partial<{
  headers: { Authorization: string };
}>;

/**
 * Staff, for the "assign to" and "reassign" pickers.
 *
 * Xano had this as a separate endpoint from the referral list above, but both
 * are the same users now, so both ask /users. The six screens that call it
 * only ever read `id` and `fname`; the rest of the row is carried across so a
 * screen that starts reading more does not find an empty object.
 */
export async function getAllStaff(
  _payload: Payload<GetAllStaffRequestType>,
): Promise<{ status: number; data: Record<string, unknown>[] }> {
  return { status: 200, data: await activeStaff() };
}

/* ------------------------------------------------------- contact search */

export type SearchContactReqType = Partial<{
  headers: { Authorization: string };
  searchQuery: string;
  contactType: "individual" | "business";
  type: string;
  paging: { page: number; per_page: number; offset?: number };
}>;

/**
 * The paged contact list behind the Retail screen.
 *
 * Xano kept contacts and customers apart and this endpoint searched the
 * contacts; there is one customers table now, and a contact is simply a
 * customer who has not bought yet. The lifecycle stage carries that
 * distinction, so the screen's customer/contact badge still resolves.
 *
 * The envelope is Xano's paging shape, which the table's pager reads
 * directly.
 */
export async function searchContact2(
  payload: Payload<SearchContactReqType> & { params?: SearchContactReqType },
): Promise<{ data: Record<string, unknown> }> {
  const query = payload.params ?? payload.data ?? {};
  const page = query.paging?.page ?? 1;
  const perPage = Math.min(query.paging?.per_page ?? 25, 100);

  const { data } = await moneefloApi.get("/customers", {
    params: {
      search: query.searchQuery?.trim() || undefined,
      page,
      perPage,
    },
  });

  const items = (data?.items ?? []) as (CustomerRow & {
    lifecycleStage?: string;
  })[];
  const total = Number(data?.total ?? items.length);
  const pageTotal = Math.max(1, Math.ceil(total / perPage));

  return {
    data: {
      items: items.map((customer) => ({
        // The customer number, not the uuid. The screen prints it in the ID
        // column and navigates with it; /customers resolves either form.
        id: String(customer.customerNo),
        contactName: customer.fullName,
        full_name: customer.fullName,
        contactNumber: customer.phone ?? "",
        phone_number: customer.phone ?? "",
        customer: customer.lifecycleStage === "customer",
        customer_types:
          customer.lifecycleStage === "customer" ? ["Customer"] : ["Contact"],
        linked_customer_id: customer.id,
      })),
      pageTotal,
      curPage: page,
      itemsReceived: items.length,
      itemsTotal: total,
      offset: (page - 1) * perPage,
      perPage,
      nextPage: page < pageTotal ? page + 1 : null,
      prevPage: page > 1 ? page - 1 : null,
    },
  };
}

/* ---------------------------------------------- corporate companies */

export type GetCompanyRequestType = Partial<{
  headers: { Authorization: string };
  category: string;
  q: string;
  paging: { page: number; per_page: number; offset?: number };
  priority_filters: string[];
}>;

/**
 * The corporate companies list.
 *
 * Xano's `company` table is an organization of type corporate now. It carried
 * a contact list, a relationship manager and a lead stage that the new model
 * does not, so those columns stay empty rather than being filled with the
 * nearest-looking thing.
 */
export async function getCompanyLeads(
  payload: Payload<GetCompanyRequestType> & { params?: GetCompanyRequestType },
): Promise<{ data: any }> {
  const query = payload.params ?? payload.data ?? {};
  const page = query.paging?.page ?? 1;
  const perPage = Math.min(query.paging?.per_page ?? 25, 100);

  const { items, total } = await onePage<
    OrganizationRow & { email?: string | null }
  >("/organizations", {
    type: "corporate",
    search: query.q?.trim() || undefined,
    page,
    perPage,
  });

  const rows: any = items.map((company) => ({
    id: company.id,
    // The screen reads the name off entity_name and the location off an
    // address list, under neither of which the organizations table holds
    // them, so both spellings go across.
    entity_name: company.name,
    company_name: company.name,
    address: company.city,
    address_id:
      company.city || company.state
        ? [{ City: company.city, State: company.state }]
        : [],
    city: company.city,
    state: company.state,
    email: company.email ?? "",
    phone: null,
    contacts_id: [],
    // The status column keys its colours by "Active", capitalised.
    status: company.status === "active" ? "Active" : company.status,
    type: company.type,
    lead_status: "",
    stage_tags: "",
    relation_manager: null,
    rm_staff: null,
  }));

  // The screen maps over this value and reads the paging off the same value.
  // Xano returned a bare array, so the pager never had anything to read; the
  // counts ride along on the array here so it works.
  const pageTotal = Math.max(1, Math.ceil(total / perPage));
  Object.assign(rows, {
    pageTotal,
    curPage: page,
    itemsReceived: items.length,
    itemsTotal: total,
    offset: (page - 1) * perPage,
    perPage,
    nextPage: page < pageTotal ? page + 1 : null,
    prevPage: page > 1 ? page - 1 : null,
  });
  return { data: rows };
}

/* ------------------------------------------- branches in the same chain */

export type GetSiblingBranchRequestType = Partial<{
  headers: { Authorization: string };
}>;

/** The branch picker on the corporate screen. Same rows as getBranchList. */
export async function getSiblingBranch(
  _payload: Payload<GetSiblingBranchRequestType>,
): Promise<{ data: any[] }> {
  const branches = await organizationsOfType("branch");
  return {
    data: branches.map((branch) => ({ id: branch.id, name: branch.name })),
  };
}

/* ------------------------------------------ a partner's own customers */

export type GetCustomerContactsRequestType = Partial<{
  headers: { Authorization: string };
}>;

/**
 * The contacts tab on the partner screen. The scope is already the caller's
 * own chain, so this is simply their customers.
 */
export async function getCustomerContacts(
  _payload: Payload<GetCustomerContactsRequestType>,
): Promise<{ data: any[] }> {
  const { items } = await onePage<{
    customerNo: number;
    fullName: string;
    phone: string | null;
    email: string | null;
    lifecycleStage: string;
    city: string | null;
  }>("/customers", { perPage: 100 });

  return {
    data: items.map((customer) => ({
      id: String(customer.customerNo),
      full_name: customer.fullName,
      phone_number: customer.phone ?? "",
      status: customer.lifecycleStage,
      city: customer.city,
      state: null,
      email: customer.email,
    })),
  };
}
